import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type ToastType = "success" | "warning" | "error";

export interface ToastMessageLine {
	label?: string;
	text: string;
}

export interface ToastItem {
	id: string;
	message: string;
	type: ToastType;
	title?: string;
	duration?: number; // ms
	lines?: ToastMessageLine[];
}

interface ToastState {
	toasts: ToastItem[];
}

const initialState: ToastState = {
	toasts: [],
};

const MAX_VISIBLE_TOASTS = 4;

function defaultDuration(type: ToastType): number {
	if (type === "error") return 6500;
	if (type === "warning") return 5000;
	return 4200;
}

function pushToast(state: ToastState, payload: Omit<ToastItem, "id">) {
	const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const duration = payload.duration ?? defaultDuration(payload.type);
	state.toasts.push({
		...payload,
		id,
		duration,
	});
}

function trimToasts(state: ToastState, cap: number) {
	if (state.toasts.length > cap) {
		state.toasts = state.toasts.slice(-cap);
	}
}

const toastSlice = createSlice({
	name: "toast",
	initialState,
	reducers: {
		addToast: (state, action: PayloadAction<Omit<ToastItem, "id">>) => {
			pushToast(state, action.payload);
			trimToasts(state, MAX_VISIBLE_TOASTS);
		},
		addToasts: (state, action: PayloadAction<Array<Omit<ToastItem, "id">>>) => {
			for (const toast of action.payload) {
				pushToast(state, toast);
			}
			trimToasts(state, Math.max(MAX_VISIBLE_TOASTS, action.payload.length));
		},
		removeToast: (state, action: PayloadAction<string>) => {
			state.toasts = state.toasts.filter((t) => t.id !== action.payload);
		},
	},
});

export const { addToast, addToasts, removeToast } = toastSlice.actions;
export default toastSlice.reducer;
